/**
 * useTaxRevenues Hook
 * 매출 관리 상태 및 액션
 */

import { useState, useCallback } from 'react';
import { TaxRepository } from '../repositories/TaxRepository';
import { TaxRevenue, CreateRevenueRequest, UpdateRevenueRequest } from '../models/Tax';

const taxRepository = new TaxRepository();

export const useTaxRevenues = () => {
  const [revenues, setRevenues] = useState<TaxRevenue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRevenues = useCallback(async (params: {
    year?: number;
    month?: number;
    search?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await taxRepository.getRevenues(params);
      setRevenues(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '매출 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  const createRevenue = useCallback(async (data: CreateRevenueRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.createRevenue(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '매출 추가 실패');
      return false;
    }
  }, []);

  const updateRevenue = useCallback(async (id: string, data: UpdateRevenueRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.updateRevenue(id, data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '매출 수정 실패');
      return false;
    }
  }, []);

  const deleteRevenue = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.deleteRevenue(id);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '매출 삭제 실패');
      return false;
    }
  }, []);

  const exportCsv = useCallback(async (year?: number, month?: number) => {
    try {
      const blob = await taxRepository.exportRevenuesCsv(year, month);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NTD_매출_${year || 'all'}_${month || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV 내보내기 실패');
    }
  }, []);

  const summary = {
    totalAmount: revenues.reduce((sum, r) => sum + r.amount, 0),
    totalVat: revenues.reduce((sum, r) => sum + r.vat, 0),
    totalSum: revenues.reduce((sum, r) => sum + r.total, 0),
    count: revenues.length,
    invoicedCount: revenues.filter(r => r.invoice_issued).length,
  };

  return {
    revenues,
    loading,
    error,
    summary,
    fetchRevenues,
    createRevenue,
    updateRevenue,
    deleteRevenue,
    exportCsv,
  };
};
